import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import {
  useGetUserProfile,
  useUpdateUser,
  useDeleteUser,
  useChangePassword,
} from '@/api/auth/useAuth';
import { getApiErrorKey } from '@/api/core';
import { useAuthStore } from '@/store/authStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useNotificationStore } from '@/store/notificationStore';
import { Button, Flex, ModalShell } from '@/components/ui';
import { Input } from '@/components/ui/common/input/Input';
import { Modal } from '@/components/ui/common/modal/Modal';
import { Text } from '@/components/ui/common/Typography';
import { FormStack, FormField, FormLabel } from '@/components/ui';
import { PhoneInput, normalizePhone } from '@/components/ui/common/PhoneInput';
import { usePolicyStore } from '@/policy/policyStore';
import { validatePasswordStrength } from '@/utils/validation/password.validator';
import { css } from '@emotion/react';

interface UserProfileModalProps {
  isVisible: boolean;
  onClose: () => void;
  userId: number;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isVisible,
  onClose,
  userId,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotificationStore();
  const { logout } = useAuthStore();
  const { reset: resetPolicy } = usePolicyStore();
  const { reset: resetSelection } = useSelectionStore();
  const { data: userProfile, refetch } = useGetUserProfile({ enabled: isVisible });
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const changePasswordMutation = useChangePassword();

  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [phoneValid, setPhoneValid] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (userProfile) {
      const phone = normalizePhone(userProfile.phone || '');
      setFormData({
        name: userProfile.name || '',
        email: userProfile.email || '',
        phone,
      });
      if (phone) {
        setPhoneValid(true);
      }
    }
  }, [userProfile]);

  const handleSave = async () => {
    try {
      await updateUserMutation.mutateAsync({ name: formData.name, phone: formData.phone });
      showSuccess('company:save_success');
      refetch();
      onClose();
    } catch (error: unknown) {
      showError(getApiErrorKey(error, 'company:error_occurred'));
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync({ id: userId });
      showSuccess('auth:withdraw_success');
      setShowDeleteConfirm(false);
      onClose();
      setTimeout(() => {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token_expiry');
        resetPolicy();
        resetSelection();
        logout();
      }, 1000);
    } catch (error: unknown) {
      showError(getApiErrorKey(error, 'company:error_occurred'));
      setShowDeleteConfirm(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('auth:password_mismatch', 'auth:passwords_do_not_match');
      return;
    }

    // 서버와 동일한 강도 규칙(8자 + 대/소문자 + 숫자 + 특수문자)을 먼저 확인한다.
    // 길이만 보면 서버에서 거부되는 값을 그대로 보내게 된다.
    if (!validatePasswordStrength(passwordData.newPassword).isValid) {
      showError('auth:password_weak', 'auth:password_complexity_required');
      return;
    }

    try {
      // confirmPassword는 위에서 이미 비교했으므로 서버로 보내지 않는다.
      // 서버 DTO가 받지 않는 속성이라 forbidNonWhitelisted 정책에 걸려 400이 된다.
      await changePasswordMutation.mutateAsync({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      showSuccess('auth:password_changed_success', 'auth:please_login_again');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordSection(false);
      onClose();

      setTimeout(() => {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token_expiry');
        resetPolicy();
        resetSelection();
        logout();
      }, 1000);
    } catch (error: unknown) {
      showError(getApiErrorKey(error, 'auth:password_change_failed'));
    }
  };

  const isPending =
    updateUserMutation.isPending ||
    deleteUserMutation.isPending ||
    changePasswordMutation.isPending;

  return (
    <>
      <ModalShell
        isOpen={isVisible && !showDeleteConfirm}
        title={t(i18nKeys.auth.my_profile)}
        onClose={onClose}
        size="md"
        footer={
          <>
            <Button
              variant="tertiaryNeutral"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isPending}
            >
              {t('auth:withdraw')}
            </Button>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <Button variant="secondary" onClick={onClose} disabled={isPending}>
                {t(i18nKeys.auth.cancel)}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isPending || (!!formData.phone && !phoneValid)}
              >
                {isPending ? t(i18nKeys.company.processing) : t(i18nKeys.company.save)}
              </Button>
            </div>
          </>
        }
      >
        <FormStack>
          <Flex direction="row" gap={2} align="start" justify="start">
            <div style={{ width: '80px' }}>
              <Text color="textSecondary" size="md" weight="medium">
                {t(i18nKeys.company.company)}
              </Text>
            </div>
            <Text color="textPrimary" size="md" weight="medium">
              {userProfile?.company_name || ''}
            </Text>
          </Flex>
          <Flex direction="row" gap={2} align="start" justify="start">
            <div style={{ width: '80px' }}>
              <Text color="textSecondary" size="md" weight="medium">
                {t(i18nKeys.auth.role)}
              </Text>
            </div>
            <Text color="textPrimary" size="md" weight="medium">
              {userProfile?.role_name || ''}
            </Text>
          </Flex>
          <Flex direction="row" gap={2} align="start" justify="start">
            <div style={{ width: '80px' }}>
              <Text color="textSecondary" size="md" weight="medium">
                {t(i18nKeys.auth.e_mail)}
              </Text>
            </div>
            <Text color="textPrimary" size="md" weight="medium">
              {userProfile?.email || ''}
            </Text>
          </Flex>
          <Flex direction="row" gap={2} align="start" justify="start">
            <div style={{ width: '90px' }}>
              <Text color="textSecondary" size="md" weight="medium">
                {t(i18nKeys.auth.name)}
              </Text>
            </div>
            <Input
              value={formData.name}
              onChange={value => setFormData({ ...formData, name: value })}
              placeholder={t(i18nKeys.auth.enter_name)}
              disabled={isPending}
              size="sm"
              fullWidth
            />
          </Flex>
          <Flex direction="row" gap={2} align="start" justify="start">
            <div style={{ width: '90px', paddingTop: '8px' }}>
              <Text color="textSecondary" size="md" weight="medium">
                {t(i18nKeys.auth.phone)}
              </Text>
            </div>
            <div style={{ flex: 1 }}>
              <PhoneInput
                value={formData.phone}
                onChange={(value, isValid) => {
                  setFormData({ ...formData, phone: value });
                  setPhoneValid(isValid);
                }}
                placeholder={t('auth:phone_placeholder')}
                disabled={isPending}
                defaultCountry="kr"
                size="sm"
                fullWidth
                hint={t('auth:phone_hint')}
              />
            </div>
          </Flex>
          <div
            css={css`
              margin-top: 1.5rem;
              padding-top: 1.5rem;
              border-top: 1px solid #e5e7eb;
            `}
          >
            <Button
              variant="tertiaryNeutral"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              disabled={isPending}
            >
              {showPasswordSection
                ? t(i18nKeys.auth.hide_password_change)
                : t(i18nKeys.auth.change_password)}
            </Button>

            {showPasswordSection && (
              <div>
                <FormStack>
                  <FormField>
                    <FormLabel>{t(i18nKeys.auth.current_password)}</FormLabel>
                    <Input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={value =>
                        setPasswordData({ ...passwordData, currentPassword: value })
                      }
                      placeholder={t(i18nKeys.auth.enter_current_password)}
                      disabled={isPending}
                      size="md"
                      fullWidth
                    />
                  </FormField>

                  <FormField>
                    <FormLabel>{t(i18nKeys.auth.new_password)}</FormLabel>
                    <Input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={value => setPasswordData({ ...passwordData, newPassword: value })}
                      placeholder={t(i18nKeys.auth.enter_new_password)}
                      disabled={isPending}
                      size="md"
                      fullWidth
                    />
                  </FormField>

                  <FormField>
                    <FormLabel>{t(i18nKeys.auth.confirm_password)}</FormLabel>
                    <Input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={value =>
                        setPasswordData({ ...passwordData, confirmPassword: value })
                      }
                      placeholder={t(i18nKeys.auth.confirm_new_password)}
                      disabled={isPending}
                      size="md"
                      fullWidth
                    />
                  </FormField>

                  <Button
                    variant="primary"
                    onClick={handleChangePassword}
                    disabled={
                      isPending ||
                      !passwordData.currentPassword ||
                      !passwordData.newPassword ||
                      !passwordData.confirmPassword
                    }
                  >
                    {changePasswordMutation.isPending
                      ? t(i18nKeys.auth.changing_password)
                      : t(i18nKeys.auth.change_password)}
                  </Button>
                </FormStack>
              </div>
            )}
          </div>
        </FormStack>
      </ModalShell>

      <Modal
        isVisible={showDeleteConfirm}
        title={t('auth:withdraw_confirm')}
        message={t('auth:withdraw_warning')}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        onClose={() => setShowDeleteConfirm(false)}
        confirmText={t(i18nKeys.company.confirm)}
        cancelText={t(i18nKeys.auth.cancel)}
        variant="danger"
        showCancel
      />
    </>
  );
};
